import React, { useMemo } from 'react';
import { BarChart3, Trophy, Target, Flame, TrendingUp, Loader2, AlertCircle } from 'lucide-react';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { Card } from '../Card';
import { StockCategoryFilter, CategoryHelpers } from '../../../types/stockCategories';
import { cn } from '../../../utils/cn';
import { useUserStatistics } from '../../../hooks/useUserStatistics';
import { useSentimentHistory } from '../../../hooks/useSentimentHistory';
import { usePremiumContext } from '../../../contexts/PremiumContext';
import { SentimentHistory } from '../../stats/SentimentHistory';

export interface StatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShare?: () => void;
  className?: string;
  category?: StockCategoryFilter;
  userId?: string;
  showCategoryContext?: boolean;
  onSignInClick?: () => void;
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  description?: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, description }) => (
  <Card className="p-4 text-center">
    <div className="flex flex-col items-center space-y-2">
      <div className="text-primary">{icon}</div>
      <div className="text-2xl font-bold text-foreground">{value}</div>
      <div className="text-sm font-medium text-foreground">{label}</div>
      {description && (
        <div className="text-xs text-muted-foreground">{description}</div>
      )}
    </div>
  </Card>
);

interface GuessDistributionProps {
  distribution: number[];
  maxValue: number;
}

const GuessDistribution: React.FC<GuessDistributionProps> = ({ distribution, maxValue }) => {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
        <BarChart3 className="h-4 w-4" />
        Guess Distribution
      </h3>
      <div className="space-y-1">
        {distribution.map((count, index) => {
          const percentage = maxValue > 0 ? (count / maxValue) * 100 : 0;
          // Display 1-4 as numbers, 5th position (index 4) as "X" for losses
          const label = index < 4 ? (index + 1).toString() : 'X';

          return (
            <div key={index} className="flex items-center gap-2 text-sm">
              <span className="w-4 text-muted-foreground font-mono">{label}</span>
              <div className="flex-1 bg-muted rounded-sm h-6 relative overflow-hidden">
                <div
                  className={cn(
                    'h-full transition-all duration-500 ease-out',
                    'bg-gradient-to-r from-primary to-primary/80'
                  )}
                  style={{ width: `${Math.max(percentage, count > 0 ? 8 : 0)}%` }}
                />
                {count > 0 && (
                  <span className="absolute inset-0 flex items-center justify-end pr-2 text-xs font-medium text-primary-foreground">
                    {count}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const StatsModal: React.FC<StatsModalProps> = ({
  isOpen,
  onClose,
  onShare,
  className,
  category,
  userId,
  showCategoryContext = true,
  onSignInClick
}) => {
  // Fetch statistics using the hook
  const { statistics, isLoading, error } = useUserStatistics({
    category,
    userId,
    autoFetch: isOpen
  });

  // Get premium status
  const { isPremium } = usePremiumContext();

  // Fetch sentiment voting history (only for premium users)
  const {
    history: sentimentHistory,
    isLoading: sentimentLoading,
    error: sentimentError
  } = useSentimentHistory({
    limit: 20,
    category,
    enabled: isOpen && isPremium
  });

  // Get category configuration for display
  const categoryConfig = category ? CategoryHelpers.getConfig(category) : null;

  const maxDistributionValue = useMemo(() => {
    if (!statistics) return 1;
    return Math.max(...statistics.guessDistribution, 1);
  }, [statistics]);

  const averageGuesses = useMemo(() => {
    if (!statistics) return 0;
    // Slots 0-3 are wins in 1-4 guesses; slot 4 ("X") counts losses
    const winDistribution = statistics.guessDistribution.slice(0, 4);
    const totalGuesses = winDistribution.reduce(
      (sum, count, index) => sum + count * (index + 1),
      0
    );
    const totalWins = winDistribution.reduce((sum, count) => sum + count, 0);

    if (totalWins === 0) return 0;
    return (totalGuesses / totalWins).toFixed(1);
  }, [statistics]);

  // Access gate for anonymous users
  if (!userId) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={showCategoryContext && categoryConfig ? `${categoryConfig.label} Statistics` : "Statistics"}
        description={showCategoryContext && categoryConfig ? `Your ${categoryConfig.label.toLowerCase()} performance` : "Your Wallstreetle performance"}
        className={cn('max-w-md', className)}
      >
        <div className="space-y-6 p-6 text-center">
          <div className="py-12">
            <BarChart3 className="h-16 w-16 mx-auto mb-4 text-gray-400 dark:text-gray-600" />
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
              {onSignInClick ? (
                <>
                  <button
                    onClick={() => {
                      onClose();
                      onSignInClick();
                    }}
                    className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                  >
                    Sign in
                  </button>
                  {' '}to view your statistics
                </>
              ) : (
                'Sign in to view your statistics'
              )}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Create a free account to track your progress, streaks, and win rate
            </p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={showCategoryContext && categoryConfig ? `${categoryConfig.label} Statistics` : "Statistics"}
      description={showCategoryContext && categoryConfig ? `Your ${categoryConfig.label.toLowerCase()} performance` : "Your Wallstreetle performance"}
      className={cn('max-w-md', className)}
    >
      <div className="space-y-6">
        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
            <p className="text-sm text-muted-foreground">Loading statistics...</p>
          </div>
        )}

        {/* Error State */}
        {!isLoading && error && !statistics && (
          <Card className="p-6 text-center border-destructive/50">
            <AlertCircle className="h-12 w-12 mx-auto mb-3 text-destructive" />
            <p className="text-sm text-destructive font-medium mb-2">Failed to load statistics</p>
            <p className="text-xs text-muted-foreground">{error}</p>
          </Card>
        )}

        {/* Statistics Content */}
        {!isLoading && statistics && (
          <>
            {/* Category Badge (if available) */}
            {showCategoryContext && categoryConfig && (
              <div className="flex justify-center">
                <div
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white shadow-sm"
                  style={{ backgroundColor: categoryConfig.color }}
                >
                  <span role="img" aria-label={categoryConfig.label}>
                    {categoryConfig.icon}
                  </span>
                  <span>{categoryConfig.label} Category</span>
                </div>
              </div>
            )}

            {/* Main Statistics Grid */}
            <div className="grid grid-cols-2 gap-3">
          <StatCard
            icon={<Target className="h-5 w-5" />}
            label="Played"
            value={statistics.gamesPlayed}
            description="Total games"
          />
          <StatCard
            icon={<TrendingUp className="h-5 w-5" />}
            label="Win %"
            value={`${statistics.winPercentage}%`}
            description="Success rate"
          />
          <StatCard
            icon={<Flame className="h-5 w-5" />}
            label="Current Streak"
            value={statistics.currentStreak}
            description="Consecutive wins"
          />
          <StatCard
            icon={<Trophy className="h-5 w-5" />}
            label="Max Streak"
            value={statistics.maxStreak}
            description="Best streak"
          />
        </div>

        {/* Additional Stats */}
        {statistics.gamesPlayed > 0 && (
          <Card className="p-4">
            <div className="text-center">
              <div className="text-lg font-semibold text-foreground">
                {averageGuesses}
              </div>
              <div className="text-sm text-muted-foreground">
                {showCategoryContext && categoryConfig
                  ? `Average guesses per ${categoryConfig.label.toLowerCase()} win`
                  : 'Average guesses per win'
                }
              </div>
            </div>
          </Card>
        )}

        {/* Guess Distribution Chart */}
        {statistics.gamesPlayed > 0 && (
          <Card className="p-4">
            <GuessDistribution
              distribution={statistics.guessDistribution}
              maxValue={maxDistributionValue}
            />
          </Card>
        )}

        {/* No Games Played Message */}
        {statistics.gamesPlayed === 0 && (
          <Card className="p-6 text-center">
            <div className="text-muted-foreground">
              <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">
                {showCategoryContext && categoryConfig
                  ? `No ${categoryConfig.label.toLowerCase()} games played yet. Start playing to see your statistics!`
                  : 'No games played yet. Start playing to see your statistics!'
                }
              </p>
            </div>
          </Card>
        )}

            {/* Sentiment Voting History (Premium Feature) */}
            <SentimentHistory
              history={sentimentHistory}
              isLoading={sentimentLoading}
              error={sentimentError}
              isPremium={isPremium}
            />

            {/* Share Button */}
            {onShare && statistics.gamesPlayed > 0 && (
              <div className="flex justify-center pt-2">
                <Button
                  onClick={onShare}
                  className="w-full"
                  aria-label="Share your statistics"
                >
                  Share Results
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default StatsModal;